import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { TapLayer } from '../mobile'
import { useMediaSrc } from '../useMediaSrc'
import { fanLabel, type Fan } from './collectionsData'
import { lastSeenText } from './FanCard'
import type { FanCardState } from './useFanJob'

interface Props {
  fan: Fan
  selected?: boolean
  /** The avatar picks the fan; shown only to people who may change lists. */
  onToggle?: () => void
  onOpen: (e: React.MouseEvent) => void
  onMessage: () => void
  job?: FanCardState
}

/** One fan on the phone (design mobile fan row): avatar, name, status, handle · last seen, Message. */
export function FanRow({ fan, selected = false, onToggle, onOpen, onMessage, job }: Props) {
  const { t, i18n } = useTranslation('library')
  const avatar = useMediaSrc(fan.avatar)
  const label = fanLabel(fan)
  const seen = lastSeenText(fan.lastSeen, i18n.language)
  const status = fan.blocked ? 'blocked' : fan.restricted ? 'restricted' : fan.status === 'expired' ? 'expired' : null
  const face = avatar.src ? <img src={avatar.src} alt="" loading="lazy" /> : label.slice(0, 1).toUpperCase()
  return (
    <div
      data-k={fan.id}
      className={`lib-frow ${selected ? 'is-on' : ''} ${job ? `is-${job.state}` : ''}`}
      role="option"
      aria-selected={selected}
      aria-label={label}
      onClick={onOpen}
    >
      <TapLayer />
      {onToggle ? (
        <button
          ref={avatar.ref as React.RefObject<HTMLButtonElement>}
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={t('select')}
          className="lib-frow-avatar"
          onClick={(e) => {
            e.stopPropagation()
            onToggle()
          }}
        >
          {selected ? <FeatherIcon name="check" size={22} /> : face}
        </button>
      ) : (
        <span ref={avatar.ref as React.RefObject<HTMLSpanElement>} className="lib-frow-avatar">
          {face}
        </span>
      )}
      <span className="lib-frow-text">
        <span className="lib-frow-name">
          <span className="lib-frow-label">{label}</span>
          {fan.verified && <FeatherIcon name="check-circle" size={15} />}
          {status && <span className={`lib-frow-status is-${status}`}>{t(`collections.status.${status}`)}</span>}
        </span>
        <span className="lib-frow-sub">
          {job?.state === 'failed' ? (
            <span className="lib-fan-error">{job.error}</span>
          ) : (
            [`@${fan.username}`, seen].filter(Boolean).join(' · ')
          )}
        </span>
      </span>
      {job?.state === 'running' ? (
        <span className="om-spin lib-frow-spinner" role="status" aria-label={t('collections.working')} />
      ) : (
        <button
          type="button"
          className="lib-mbtn lib-mbtn--accent"
          aria-label={t('collections.message')}
          title={t('collections.message')}
          onClick={(e) => {
            e.stopPropagation()
            onMessage()
          }}
        >
          <FeatherIcon name="message-square" size={20} />
        </button>
      )}
    </div>
  )
}
